/* =========================================================
   BOX IMPORT — Intro cinemática
   Archivo: assets/box-import-intro.js
   Al terminar dispara el evento "bi:intro-done" en document.
   ========================================================= */
(() => {
  if (customElements.get('bi-intro')) return;

  const INTRO_READY_MS = 2000; // la caja ya aterrizó y se puede abrir
  const SOUND_KEY = 'bi-intro-sound';

  /* ---------- Efectos de sonido sintetizados (Web Audio) ----------
     No se descarga ningún archivo: los sonidos se generan en el momento.
     Los navegadores sólo permiten sonido después de que el cliente
     interactúa con la página (un toque o un clic).
     iPhone: con la llave de silencio activada, el audio web se calla salvo que la página
     se declare como "reproducción multimedia". Lo hacemos con la Audio Session API
     (Safari 16.4+) y, en iPhones más viejos, con una pista silenciosa en loop. */
  class SoundFX {
    constructor() {
      this.ctx = null;
      this.muted = false;
      try { this.muted = localStorage.getItem(SOUND_KEY) === 'off'; } catch (e) { /* sin storage */ }
    }

    // Crea el motor de audio en silencio (lo más costoso es despertar el dispositivo de audio).
    prepare() {
      if (this.muted || this.ctx) return;
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      // Cadena: efectos → master → (seco + reverb) → limitador → parlantes
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.9;
      this.limiter = this.ctx.createDynamicsCompressor();
      this.limiter.threshold.value = -10;
      this.limiter.knee.value = 6;
      this.limiter.ratio.value = 8;
      this.limiter.attack.value = 0.003;
      this.limiter.release.value = 0.2;
      this.reverb = this.ctx.createConvolver();
      this.reverb.buffer = this.impulse(1.4, 2.6);
      this.wet = this.ctx.createGain();
      this.wet.gain.value = 0.22;
      this.master.connect(this.limiter);
      this.master.connect(this.reverb).connect(this.wet).connect(this.limiter);
      this.limiter.connect(this.ctx.destination);
    }

    // Debe llamarse dentro de un toque/clic del cliente
    unlock() {
      if (this.muted) return;
      this.playThroughSilentMode();
      this.prepare();
      if (this.ctx && this.ctx.state === 'suspended') this.ctx.resume();
      this.unlocked = !!this.ctx;
    }

    // Que suene aunque el iPhone esté en modo silencio
    playThroughSilentMode() {
      try {
        if (navigator.audioSession) navigator.audioSession.type = 'playback';
      } catch (e) { /* navegador sin Audio Session API */ }
      if (this.silentTrack) return;
      try {
        const track = new Audio(this.silentWavUrl());
        track.loop = true;
        track.setAttribute('playsinline', '');
        track.preload = 'auto';
        const p = track.play();
        if (p && p.catch) p.catch(() => {});
        this.silentTrack = track;
      } catch (e) { /* sin elemento de audio */ }
    }

    // Pista WAV de medio segundo en silencio, generada en memoria (sin archivos externos)
    silentWavUrl() {
      const rate = 8000;
      const samples = rate / 2;
      const buf = new ArrayBuffer(44 + samples);
      const v = new DataView(buf);
      const str = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
      str(0, 'RIFF'); v.setUint32(4, 36 + samples, true); str(8, 'WAVE');
      str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
      v.setUint32(24, rate, true); v.setUint32(28, rate, true); v.setUint16(32, 1, true); v.setUint16(34, 8, true);
      str(36, 'data'); v.setUint32(40, samples, true);
      for (let i = 0; i < samples; i++) v.setUint8(44 + i, 128);
      return URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
    }

    // Libera el modo "multimedia" al terminar la intro
    release() {
      if (this.silentTrack) { this.silentTrack.pause(); this.silentTrack = null; }
    }

    get ready() {
      return this.active && this.ctx.state === 'running';
    }

    // Sonido activado por el cliente (aunque el navegador todavía esté arrancando el audio)
    get active() {
      return !this.muted && !!this.ctx && this.unlocked === true;
    }

    setMuted(muted) {
      this.muted = muted;
      try { localStorage.setItem(SOUND_KEY, muted ? 'off' : 'on'); } catch (e) { /* sin storage */ }
      if (this.master) this.master.gain.value = muted ? 0 : 0.9;
      if (!muted) this.unlock();
    }

    /* ---------- Piezas básicas ---------- */
    impulse(seconds, decay) {
      const rate = this.ctx.sampleRate;
      const len = Math.floor(rate * seconds);
      const buf = this.ctx.createBuffer(2, len, rate);
      for (let ch = 0; ch < 2; ch++) {
        const d = buf.getChannelData(ch);
        for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
      }
      return buf;
    }

    noise(duration) {
      const len = Math.ceil(this.ctx.sampleRate * duration);
      const buffer = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      return src;
    }

    env(gainNode, t, peak, attack, release) {
      gainNode.gain.setValueAtTime(0.0001, t);
      gainNode.gain.exponentialRampToValueAtTime(peak, t + attack);
      gainNode.gain.exponentialRampToValueAtTime(0.0001, t + attack + release);
    }

    // Paneo estéreo (de izquierda a derecha), si el navegador lo soporta
    pan(from, to, t, dur) {
      if (!this.ctx.createStereoPanner) return this.ctx.createGain();
      const p = this.ctx.createStereoPanner();
      p.pan.setValueAtTime(from, t);
      p.pan.linearRampToValueAtTime(to, t + dur);
      return p;
    }

    tone(type, freq, t, dur, peak, dest, glideTo) {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = type;
      o.frequency.setValueAtTime(freq, t);
      if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t + dur);
      this.env(g, t, peak, Math.min(0.01, dur / 4), dur);
      o.connect(g).connect(dest || this.master);
      o.start(t);
      o.stop(t + dur + 0.05);
    }

    /* ---------- Efectos de la escena ---------- */

    // Toque en "Tocá para entrar": un "bloop" amable
    tap() {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      this.tone('sine', 520, t, 0.16, 0.35, null, 880);
      this.tone('triangle', 1040, t + 0.05, 0.12, 0.12);
    }

    // Motor del camión: dos sierras desafinadas + sub grave + ronroneo + ruido de ruta, con paneo
    engine(duration = 1, from = 45, to = 95, panFrom = -0.8, panTo = 0) {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      const out = this.ctx.createGain();
      const lp = this.ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(420, t);
      lp.frequency.linearRampToValueAtTime(900, t + duration * 0.7);
      out.gain.setValueAtTime(0.0001, t);
      out.gain.exponentialRampToValueAtTime(0.5, t + 0.15);
      out.gain.setValueAtTime(0.5, t + duration * 0.7);
      out.gain.exponentialRampToValueAtTime(0.0001, t + duration);
      lp.connect(out).connect(this.pan(panFrom, panTo, t, duration)).connect(this.master);
      // ronroneo: modulación rápida del volumen (pistones)
      const lfo = this.ctx.createOscillator();
      const lfoGain = this.ctx.createGain();
      lfo.frequency.setValueAtTime(18, t);
      lfo.frequency.linearRampToValueAtTime(34, t + duration);
      lfoGain.gain.value = 0.18;
      lfo.connect(lfoGain).connect(out.gain);
      lfo.start(t);
      lfo.stop(t + duration + 0.05);
      [[0, 'sawtooth', 0.32], [7, 'sawtooth', 0.32], [-1200, 'sine', 0.5]].forEach(([detune, type, level]) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = type;
        o.detune.value = detune;
        o.frequency.setValueAtTime(from, t);
        o.frequency.exponentialRampToValueAtTime(to, t + duration * 0.8);
        g.gain.value = level;
        o.connect(g).connect(lp);
        o.start(t);
        o.stop(t + duration + 0.05);
      });
      // rugido de ruta (ruido grave)
      const rumble = this.noise(duration);
      const rlp = this.ctx.createBiquadFilter();
      rlp.type = 'lowpass';
      rlp.frequency.value = 180;
      const rg = this.ctx.createGain();
      rg.gain.value = 0.35;
      rumble.connect(rlp).connect(rg).connect(lp);
      rumble.start(t);
    }

    // Bocina "bip-bip" del repartidor
    horn() {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      [0, 0.2].forEach((d) => {
        const bp = this.ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 900;
        bp.Q.value = 0.8;
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.0001, t + d);
        g.gain.exponentialRampToValueAtTime(0.28, t + d + 0.015);
        g.gain.setValueAtTime(0.28, t + d + 0.12);
        g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.16);
        bp.connect(g).connect(this.master);
        [392, 494].forEach((f) => {
          const o = this.ctx.createOscillator();
          o.type = 'square';
          o.frequency.value = f;
          o.connect(bp);
          o.start(t + d);
          o.stop(t + d + 0.18);
        });
      });
    }

    // Frenada: chirrido + derrape de gomas
    brake() {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      const squeal = this.ctx.createOscillator();
      const sg = this.ctx.createGain();
      const sbp = this.ctx.createBiquadFilter();
      squeal.type = 'sawtooth';
      squeal.frequency.setValueAtTime(2300, t);
      squeal.frequency.exponentialRampToValueAtTime(1500, t + 0.32);
      sbp.type = 'bandpass';
      sbp.frequency.value = 2000;
      sbp.Q.value = 6;
      this.env(sg, t, 0.07, 0.02, 0.32);
      squeal.connect(sbp).connect(sg).connect(this.master);
      squeal.start(t);
      squeal.stop(t + 0.4);
      const skid = this.noise(0.4);
      const band = this.ctx.createBiquadFilter();
      const gain = this.ctx.createGain();
      band.type = 'bandpass';
      band.frequency.setValueAtTime(3200, t);
      band.frequency.exponentialRampToValueAtTime(1400, t + 0.35);
      band.Q.value = 4;
      this.env(gain, t, 0.3, 0.02, 0.36);
      skid.connect(band).connect(gain).connect(this.master);
      skid.start(t);
    }

    // Whoosh: aire que pasa (salto de la caja / zoom), con paneo
    whoosh(duration = 0.4, from = 300, to = 2200, peak = 0.35, panFrom = -0.4, panTo = 0.4) {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      const src = this.noise(duration + 0.05);
      const band = this.ctx.createBiquadFilter();
      const gain = this.ctx.createGain();
      band.type = 'bandpass';
      band.Q.value = 1.4;
      band.frequency.setValueAtTime(from, t);
      band.frequency.exponentialRampToValueAtTime(to, t + duration);
      this.env(gain, t, peak, duration * 0.55, duration * 0.45);
      src.connect(band).connect(gain).connect(this.pan(panFrom, panTo, t, duration)).connect(this.master);
      src.start(t);
    }

    // Aterrizaje: golpe grave profundo + cartón + traqueteo del contenido
    thud() {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      this.tone('sine', 120, t, 0.38, 1, null, 38);          // boom
      this.tone('triangle', 220, t, 0.12, 0.35, null, 90);   // cuerpo de la caja
      const src = this.noise(0.18);                           // cartón
      const bp = this.ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 700;
      bp.Q.value = 1.2;
      const g2 = this.ctx.createGain();
      this.env(g2, t, 0.55, 0.003, 0.14);
      src.connect(bp).connect(g2).connect(this.master);
      src.start(t);
      [0.09, 0.15, 0.22].forEach((d, i) => {                 // traqueteo
        const r = this.noise(0.05);
        const hp = this.ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 2500;
        const rg = this.ctx.createGain();
        this.env(rg, t + d, 0.12 / (i + 1), 0.002, 0.04);
        r.connect(hp).connect(rg).connect(this.master);
        r.start(t + d);
      });
    }

    // Apertura: "pop" de las solapas + arpegio brillante + chisporroteo de confeti
    open() {
      if (!this.ready) return;
      const t = this.ctx.currentTime;
      this.tone('sine', 760, t, 0.1, 0.6, null, 170);
      [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568].forEach((freq, i) => {
        const start = t + 0.06 + i * 0.055;
        this.tone('triangle', freq, start, 0.65, 0.2);
        this.tone('sine', freq * 2, start, 0.4, 0.06);
      });
      for (let i = 0; i < 14; i++) {                          // confeti
        const d = 0.08 + Math.random() * 0.5;
        const c = this.noise(0.03);
        const hp = this.ctx.createBiquadFilter();
        hp.type = 'highpass';
        hp.frequency.value = 3000 + Math.random() * 3000;
        const g = this.ctx.createGain();
        this.env(g, t + d, 0.05 + Math.random() * 0.06, 0.002, 0.03);
        c.connect(hp).connect(g).connect(this.pan(Math.random() * 2 - 1, Math.random() * 2 - 1, t + d, 0.05)).connect(this.master);
        c.start(t + d);
      }
    }

    // Entrada a la tienda: whoosh + acorde "¡listo!" de la marca
    enter() {
      if (!this.ready) return;
      this.whoosh(0.55, 250, 3200, 0.3, 0, 0);
      const t = this.ctx.currentTime + 0.32;
      [[392, 0], [523.25, 0.07], [659.25, 0.14], [783.99, 0.21]].forEach(([f, d]) => {
        this.tone('triangle', f, t + d, 0.9 - d, 0.16);
      });
      this.tone('sine', 196, t, 0.9, 0.18);
    }
  }

  class BiIntro extends HTMLElement {
    connectedCallback() {
      this.ac = new AbortController();
      const opts = { signal: this.ac.signal };
      this.openBtn = this.querySelector('[data-intro-open]');
      this.soundBtn = this.querySelector('[data-intro-sound]');
      this.startedAt = performance.now();
      this.cues = [];
      this.sfx = this.sfx || new SoundFX();
      this.syncSoundButton();

      this.openBtn?.addEventListener('click', () => this.open(), opts);
      this.querySelector('[data-intro-skip]')?.addEventListener('click', () => this.leave(), opts);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.leave(); }, opts);

      // Precalentamos el audio en silencio con el primer movimiento (evita un tirón al abrir la caja)
      const warm = () => this.sfx.prepare();
      this.addEventListener('pointermove', warm, { once: true, passive: true, signal: this.ac.signal });
      this.addEventListener('touchstart', warm, { once: true, passive: true, signal: this.ac.signal });

      // Cualquier toque dentro de la intro habilita el audio (regla de los navegadores)
      this.addEventListener('pointerdown', (e) => {
        if (e.target.closest('[data-intro-sound]')) return;
        this.sfx.unlock();
        this.syncSoundButton();
      }, opts);
      this.soundBtn?.addEventListener('click', () => {
        this.sfx.setMuted(this.sfx.active);
        this.syncSoundButton();
      }, opts);

      // Editor de Shopify: se reproduce al seleccionar la sección
      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (e) => {
          if (this.closest('.shopify-section')?.id === `shopify-section-${e.detail.sectionId}`) this.replay();
        }, opts);
        return;
      }
      if (this.hidden) return;
      // Con pantalla de inicio, la cinemática espera el toque (que además habilita el sonido)
      this.gate = this.querySelector('[data-intro-gate]');
      if (this.gate && this.classList.contains('is-waiting')) {
        document.documentElement.classList.add('bi-intro-active', 'bi-intro-lock');
        const go = () => this.begin();
        this.gate.addEventListener('click', go, opts);
        this.gate.focus({ preventScroll: true });
        return;
      }
      this.start();
    }

    // Toque en "Tocá para entrar": sonido encendido siempre y arranca todo desde cero
    begin() {
      this.sfx.muted = false;
      this.sfx.unlock();
      if (this.sfx.master) this.sfx.master.gain.value = 0.9;
      this.syncSoundButton();
      this.sfx.tap();
      this.classList.remove('is-waiting');
      this.classList.add('is-started', 'is-auto');
      this.autoEnter = true; // un solo toque: la caja se abre sola y entra a la tienda
      this.start();
    }

    disconnectedCallback() {
      this.ac?.abort();
      clearTimeout(this.focusTimer);
      clearTimeout(this.autoTimer);
      this.cues?.forEach(clearTimeout);
      this.unlock();
    }

    syncSoundButton() {
      if (!this.soundBtn) return;
      const on = this.sfx.active;
      this.soundBtn.setAttribute('aria-pressed', String(on));
      this.soundBtn.classList.toggle('is-on', on);
    }

    // Línea de tiempo del sonido, sincronizada con las animaciones del CSS
    scheduleSounds() {
      const at = (ms, fn) => this.cues.push(setTimeout(fn, ms));
      at(100, () => this.sfx.engine(1.0, 45, 95, -0.9, 0));    // llega el camión desde la izquierda
      at(620, () => this.sfx.horn());                           // bocina del repartidor
      at(1050, () => this.sfx.brake());                         // frena
      at(1180, () => this.sfx.whoosh(0.45, 400, 1800, 0.25));   // salta la caja
      at(1720, () => this.sfx.thud());                          // aterriza
      at(1900, () => this.sfx.engine(0.75, 60, 120, 0, 0.9));   // el camión se va hacia la derecha
    }

    start() {
      document.documentElement.classList.add('bi-intro-active', 'bi-intro-lock');
      this.startedAt = performance.now();
      this.scheduleSounds();
      this.focusTimer = setTimeout(() => this.openBtn?.focus({ preventScroll: true }), INTRO_READY_MS);
      const auto = Number(this.dataset.autoOpen) || 0;
      if (this.autoEnter) this.autoTimer = setTimeout(() => this.open(), INTRO_READY_MS + 600);
      else if (auto > 0) this.autoTimer = setTimeout(() => this.open(), INTRO_READY_MS + auto * 1000);
    }

    replay() {
      // Reinicia todas las animaciones clonando el contenido
      this.classList.remove('is-opening', 'is-leaving', 'is-waiting');
      this.classList.add('is-started');
      this.hidden = false;
      this.done = false;
      const fresh = this.innerHTML;
      this.innerHTML = fresh;
      this.disconnectedCallback();
      this.connectedCallback();
      this.start();
    }

    open() {
      if (this.done || this.classList.contains('is-opening')) return;
      // Si tocan antes de que aterrice la caja, saltamos directo al final
      if (performance.now() - this.startedAt < INTRO_READY_MS - 400) return this.leave();
      clearTimeout(this.autoTimer);
      this.sfx.unlock();
      this.sfx.open();
      this.classList.add('is-opening');
      this.burst();
      setTimeout(() => this.leave(), 750);
    }

    burst() {
      const box = this.querySelector('.bi-intro__drop');
      if (!box) return;
      const host = this.getBoundingClientRect();
      const r = box.getBoundingClientRect();
      const x = r.left + r.width / 2 - host.left;
      const y = r.top + r.height * 0.35 - host.top;
      const colors = ['#1F2B40', '#FFFFFF', '#E8A01E', '#2A3954', '#FCE7A0', '#F5CF52'];
      for (let i = 0; i < 32; i++) {
        const p = document.createElement('span');
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.4; // hacia arriba
        const dist = 140 + Math.random() * 260;
        p.className = 'bi-intro__confetti';
        p.style.left = `${x}px`;
        p.style.top = `${y}px`;
        p.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        p.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
        p.style.setProperty('--r', `${Math.random() * 900 - 450}deg`);
        p.style.setProperty('--size', `${8 + Math.random() * 10}px`);
        p.style.setProperty('--c', colors[i % colors.length]);
        this.appendChild(p);
        p.addEventListener('animationend', () => p.remove());
      }
    }

    leave() {
      if (this.done) return;
      this.done = true;
      clearTimeout(this.autoTimer);
      this.cues.forEach(clearTimeout);
      this.sfx.enter(); // la cámara entra a la caja + acorde de bienvenida
      setTimeout(() => this.sfx.release(), 1800); // deja terminar el acorde y libera el modo multimedia
      this.classList.add('is-leaving');
      this.remember();
      setTimeout(() => {
        this.unlock();
        document.dispatchEvent(new CustomEvent('bi:intro-done'));
        if (this.dataset.designMode !== 'true') this.hidden = true;
      }, 600);
    }

    remember() {
      try {
        const freq = this.dataset.frequency;
        if (freq === 'session') sessionStorage.setItem('bi-intro-seen', '1');
        if (freq === 'once') localStorage.setItem('bi-intro-seen', '1');
      } catch (e) { /* sin storage */ }
    }

    unlock() {
      document.documentElement.classList.remove('bi-intro-active', 'bi-intro-lock');
    }
  }

  customElements.define('bi-intro', BiIntro);
})();
